"use client"

import { useSearchParams, useRouter, usePathname } from "next/navigation"
import { useState, useEffect, useCallback } from "react"
import { BusinessBrainCenterSurface } from "@/components/brain/business-brain-center-surface"
import { BrainRail } from "@/components/brain/brain-rail"
import { CenterColumn } from "@/components/layout/center-column"
import Link from "next/link"

export type BrainSelection =
  | { type: "overview" }
  | { type: "domain"; slug: string; label: string }
  | { type: "node"; domainSlug: string; nodeSlug: string; label: string }
  | { type: "memory"; id: string; title: string; domainSlug?: string; nodeSlug?: string }

const VALID_DOMAINS = [
  "identity",
  "team",
  "audience",
  "competition",
  "offerings",
  "brand",
  "marketing",
  "sales",
  "partnerships",
  "operations",
  "technology",
  "finance",
  "strategy",
]

export function BrainPageContent() {
  const searchParams = useSearchParams()
  const router = useRouter()
  const pathname = usePathname()

  const [selection, setSelection] = useState<BrainSelection>({ type: "overview" })

  useEffect(() => {
    const memoryId = searchParams.get("memory")
    const scope = searchParams.get("scope")

    if (memoryId) {
      // Memory focus - highest priority
      setSelection({ type: "memory", id: memoryId, title: "Memory" })
    } else if (scope) {
      if (scope.startsWith("domain:")) {
        const slug = scope.replace("domain:", "")
        // Validate domain exists
        if (VALID_DOMAINS.includes(slug)) {
          setSelection({ type: "domain", slug, label: slug })
        } else {
          // Invalid domain - degrade to overview
          setSelection({ type: "overview" })
        }
      } else if (scope.startsWith("node:")) {
        const parts = scope.replace("node:", "").split("/")
        const domainSlug = parts[0] || ""
        const nodeSlug = parts[1] || parts[0] || ""

        // Validate domain exists
        if (VALID_DOMAINS.includes(domainSlug)) {
          setSelection({
            type: "node",
            domainSlug,
            nodeSlug,
            label: nodeSlug,
          })
        } else if (VALID_DOMAINS.includes(nodeSlug)) {
          // Malformed but recoverable - treat as domain
          setSelection({ type: "domain", slug: nodeSlug, label: nodeSlug })
        } else {
          // Invalid - degrade to overview
          setSelection({ type: "overview" })
        }
      } else {
        // Unknown scope format - degrade to overview
        setSelection({ type: "overview" })
      }
    }
    // Note: if no params, keep current selection (don't force overview)
  }, [searchParams])

  const updateUrlForSelection = useCallback(
    (sel: BrainSelection) => {
      let newUrl = pathname

      switch (sel.type) {
        case "overview":
          newUrl = pathname // Clean URL, no params
          break
        case "domain":
          newUrl = `${pathname}?scope=domain:${sel.slug}`
          break
        case "node":
          newUrl = `${pathname}?scope=node:${sel.domainSlug}/${sel.nodeSlug}`
          break
        case "memory":
          newUrl = `${pathname}?memory=${sel.id}`
          break
      }

      // Use replace to avoid bloating history, but still enable copy-link
      router.replace(newUrl, { scroll: false })
    },
    [pathname, router],
  )

  const handleSelectionChange = useCallback(
    (newSelection: BrainSelection) => {
      setSelection(newSelection)
      updateUrlForSelection(newSelection)
    },
    [updateUrlForSelection],
  )

  const getAdvisorLink = () => {
    const base = "/advisor?new=1&from=brain"
    switch (selection.type) {
      case "overview":
        return `${base}&scope=overview`
      case "domain":
        return `${base}&scope=domain:${selection.slug}`
      case "node":
        return `${base}&scope=node:${selection.domainSlug}/${selection.nodeSlug}`
      case "memory":
        return `${base}&scope=memory:${selection.id}`
      default:
        return `${base}&scope=overview`
    }
  }

  const getSurfaceState = (): "overview" | "scope" | "memory" => {
    switch (selection.type) {
      case "memory":
        return "memory"
      case "domain":
      case "node":
        return "scope"
      default:
        return "overview"
    }
  }

  return (
    <div className="flex h-full flex-col lg:flex-row overflow-hidden relative">
      {/* Center column */}
      <div className="flex-1 flex flex-col min-h-full" style={{ backgroundColor: "var(--surface)" }}>
        {/* Phase 1: Breadcrumb navigation header (no duplicate CTA) */}
        <div className="hidden lg:flex h-16 items-center justify-between px-6 md:px-10 lg:px-12 shrink-0 border-b border-white/5">
          <nav className="flex items-center gap-2 text-sm font-sans">
            <span className="text-foreground font-medium">Memories</span>
            {selection.type !== "overview" && (
              <>
                <span className="text-muted-foreground">/</span>
                <span className="text-muted-foreground capitalize">
                  {selection.type === "domain" && selection.label}
                  {selection.type === "node" && `${selection.domainSlug} / ${selection.label}`}
                  {selection.type === "memory" && "Memory"}
                </span>
              </>
            )}
          </nav>
          {/* Help icon placeholder - navigation only, no CTA */}
          <button
            type="button"
            className="shrink-0 h-8 w-8 rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-white/5 transition-colors"
            aria-label="Help"
          >
            <span className="text-sm">?</span>
          </button>
        </div>

        <div className="flex-1 overflow-hidden flex flex-col pb-3 relative">
          <div className="h-full overflow-y-auto pt-8">
            <CenterColumn>
              <BusinessBrainCenterSurface
                state={getSurfaceState()}
                scope={
                  selection.type === "domain"
                    ? { type: "domain", slug: selection.slug, label: selection.label }
                    : selection.type === "node"
                      ? { type: "node", slug: selection.nodeSlug, label: selection.label }
                      : undefined
                }
                memory={selection.type === "memory" ? { id: selection.id, title: selection.title } : undefined}
                onOpenMemory={(id) => handleSelectionChange({ type: "memory", id, title: "Memory" })}
                onAddMemory={() => {
                  /* Mock - would open create flow */
                }}
                onPrefillDraft={() => {
                  /* Mock - returns structured payload */
                }}
              />
            </CenterColumn>
          </div>
        </div>
      </div>

      <BrainRail selection={selection} onSelectionChange={handleSelectionChange} />
    </div>
  )
}
